import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getNetworkReconnectionManager } from '../utils/networkReconnectionManager';

export interface ServiceHealth {
  name: string;
  displayName: string;
  status: 'healthy' | 'unhealthy' | 'checking';
  responseTime?: number;
  lastChecked?: Date;
  error?: string;
}

export type SystemHealthStatus = 'online' | 'degraded' | 'offline' | 'checking';

interface SystemHealthContextType {
  services: ServiceHealth[];
  overallStatus: SystemHealthStatus;
  healthyCount: number;
  unhealthyCount: number;
  isChecking: boolean;
  checkHealth: () => Promise<void>;
  triggerImmediateCheck: () => Promise<void>;
  lastChecked: Date | null;
}

const SystemHealthContext = createContext<SystemHealthContextType | null>(null);

const INITIAL_SERVICES: ServiceHealth[] = [
  { name: 'condo-manager', displayName: 'API (Condo Manager)', status: 'checking' },
  { name: 'iot-connect', displayName: 'IoT Connect', status: 'checking' },
  { name: 'keycloak', displayName: 'Autenticação (Keycloak)', status: 'checking' },
  { name: 'sse', displayName: 'Eventos em Tempo Real (SSE)', status: 'checking' },
];

// Check interval: 60 seconds
const HEALTH_CHECK_INTERVAL = 60000;

export const SystemHealthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [services, setServices] = useState<ServiceHealth[]>(INITIAL_SERVICES);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const checkHealth = useCallback(async () => {
    setIsChecking(true);

    const checkService = async (
      name: string,
      displayName: string,
      checkFn: () => Promise<{ ok: boolean; responseTime: number; error?: string }>
    ): Promise<ServiceHealth> => {
      try {
        const result = await checkFn();
        return {
          name,
          displayName,
          status: result.ok ? 'healthy' : 'unhealthy',
          responseTime: result.responseTime,
          lastChecked: new Date(),
          error: result.error,
        };
      } catch (error) {
        return {
          name,
          displayName,
          status: 'unhealthy',
          lastChecked: new Date(),
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    };

    const healthChecks = await Promise.all([
      // Check Condo Manager API
      checkService('condo-manager', 'API (Condo Manager)', async () => {
        const startTime = Date.now();
        const response = await fetch('/api/v1/health');
        const responseTime = Date.now() - startTime;
        return { ok: response.ok, responseTime };
      }),

      // Check IoT Connect
      checkService('iot-connect', 'IoT Connect', async () => {
        const startTime = Date.now();
        const response = await fetch('/iot/health');
        const responseTime = Date.now() - startTime;
        return { ok: response.ok, responseTime };
      }),

      // Check Keycloak
      checkService('keycloak', 'Autenticação (Keycloak)', async () => {
        const startTime = Date.now();
        const response = await fetch('/auth/realms/panthercondos');
        const responseTime = Date.now() - startTime;
        return { ok: response.ok, responseTime };
      }),

      // Check SSE (runs on same service as condo-manager)
      checkService('sse', 'Eventos em Tempo Real (SSE)', async () => {
        const startTime = Date.now();
        const response = await fetch('/api/v1/health');
        const responseTime = Date.now() - startTime;
        return { ok: response.ok, responseTime };
      }),
    ]);

    setServices(healthChecks);
    setLastChecked(new Date());
    setIsChecking(false);
  }, []);

  /**
   * Trigger an immediate health check (bypassing the interval)
   * Useful for checking health after connection restoration
   */
  const triggerImmediateCheck = useCallback(async () => {
    console.log('[SystemHealth] Triggering immediate health check');
    await checkHealth();
  }, [checkHealth]);

  // Check health on mount and periodically
  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [checkHealth]);

  // Subscribe to NetworkReconnectionManager to trigger immediate check on connection restoration
  useEffect(() => {
    const manager = getNetworkReconnectionManager();

    const handleConnectionRestored = () => {
      console.log('[SystemHealth] Connection restored, triggering immediate health check');
      triggerImmediateCheck();
    };

    manager.on('connectionRestored', handleConnectionRestored);

    return () => {
      manager.off('connectionRestored', handleConnectionRestored);
    };
  }, [triggerImmediateCheck]);

  // Calculate overall status
  const healthyCount = services.filter(s => s.status === 'healthy').length;
  const unhealthyCount = services.filter(s => s.status === 'unhealthy').length;
  const checkingCount = services.filter(s => s.status === 'checking').length;

  let overallStatus: SystemHealthStatus;
  if (checkingCount === services.length) {
    overallStatus = 'checking';
  } else if (unhealthyCount === services.length) {
    overallStatus = 'offline';
  } else if (unhealthyCount > 0) {
    overallStatus = 'degraded';
  } else {
    overallStatus = 'online';
  }

  return (
    <SystemHealthContext.Provider
      value={{
        services,
        overallStatus,
        healthyCount,
        unhealthyCount,
        isChecking,
        checkHealth,
        triggerImmediateCheck,
        lastChecked,
      }}
    >
      {children}
    </SystemHealthContext.Provider>
  );
};

export const useSystemHealth = (): SystemHealthContextType => {
  const context = useContext(SystemHealthContext);
  if (!context) {
    throw new Error('useSystemHealth must be used within a SystemHealthProvider');
  }
  return context;
};
